import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ShieldCheck, AlertTriangle, Stamp, Check, X, Flame } from 'lucide-react';
import { ASSETS } from '../assets/assets';
import { SectionTitle } from './ChineseDecor';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';

interface LocalizedField { 'zh-HK'?: string; 'zh-CN'?: string; en?: string; }
interface Point { icon: string; title: string | LocalizedField; desc: string | LocalizedField; }
interface CompareRow { label: string | LocalizedField; genuine: string | LocalizedField; fake: string | LocalizedField; }

const iconMap: Record<string, React.ReactNode> = {
  shield: <ShieldCheck className="w-6 h-6" />,
  alert: <AlertTriangle className="w-6 h-6" />,
  stamp: <Stamp className="w-6 h-6" />,
};

interface AuthenticProps {
  content?: Record<string, unknown>;
}

const Authentic: React.FC<AuthenticProps> = ({ content = {} }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;
  const sealImage = (content.sealImage as string) || ASSETS.logo;

  const eyebrow = getLocalizedText(content.eyebrow, lang, t('authentic.eyebrow'));
  const title = getLocalizedText(content.title, lang, t('authentic.title'));
  const intro = getLocalizedText(content.intro, lang, t('authentic.intro'));
  const compareTitle = getLocalizedText(content.compareTitle, lang, t('authentic.compare_title'));
  const genuineLabel = getLocalizedText(content.genuineLabel, lang, t('authentic.genuine'));
  const fakeLabel = getLocalizedText(content.fakeLabel, lang, t('authentic.fake'));
  const note = getLocalizedText(content.note, lang, t('authentic.note'));

  const legacyPoints = t('authentic.points', { returnObjects: true }) as Point[];
  const legacyCompare = t('authentic.compare', { returnObjects: true }) as CompareRow[];
  const points = (content.points as Point[] | undefined) || legacyPoints;
  const compare = (content.compare as CompareRow[] | undefined) || legacyCompare;

  return (
    <section id="authentic" className="relative py-12 md:py-16 bg-brand-ink text-brand-paper overflow-hidden">
      <div className="absolute inset-0 bg-cloud opacity-[0.15] pointer-events-none" />
      {/* warning accent stripe */}
      <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-brand-cinnabar to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle eyebrow={eyebrow} title={title} light />

        <div className="grid lg:grid-cols-[280px_1fr] gap-12 items-center mt-14">
          {/* Seal */}
          <div className="flex flex-col items-center">
            <div className="relative w-52 h-52 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-brand-gold/50" />
              <div className="absolute inset-2 rounded-full border border-brand-gold/20" />
              <img src={sealImage} alt={genuineLabel} className="w-40 h-40 object-contain seal-pulse drop-shadow-[0_0_30px_rgba(197,160,89,0.55)]" />
            </div>
            <p className="mt-4 text-brand-gold font-serif tracking-[0.2em] text-sm">{genuineLabel}</p>
          </div>

          {/* Intro + points */}
          <div>
            <p className="text-brand-paper/75 font-sans leading-loose text-[15px] max-w-3xl">{intro}</p>
            <div className="grid sm:grid-cols-3 gap-6 mt-10">
              {points.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`p-6 border ${p.icon === 'alert' ? 'border-brand-cinnabar/50 bg-brand-cinnabar/10' : 'border-brand-paper/10 bg-brand-paper/[0.03]'}`}
                >
                  <div className={`${p.icon === 'alert' ? 'text-brand-cinnabar' : 'text-brand-gold'} mb-4`}>
                    {iconMap[p.icon]}
                  </div>
                  <h3 className="font-serif text-lg text-brand-paper mb-2">{getLocalizedText(p.title, lang, legacyPoints?.[i]?.title)}</h3>
                  <p className="text-brand-paper/60 text-[13px] font-sans leading-relaxed">{getLocalizedText(p.desc, lang, legacyPoints?.[i]?.desc)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison table */}
        <div className="mt-16 max-w-4xl mx-auto">
          <h3 className="text-center font-serif text-xl text-brand-gold mb-8 tracking-[0.15em]">
            {compareTitle}
          </h3>
          <div className="overflow-hidden border border-brand-paper/10 rounded-sm">
            <div className="grid grid-cols-3 bg-brand-paper/[0.06] text-xs sm:text-sm font-serif">
              <div className="p-4" />
              <div className="p-4 text-center text-brand-gold border-l border-brand-paper/10 flex items-center justify-center gap-1.5">
                <Check className="w-4 h-4" /> {genuineLabel}
              </div>
              <div className="p-4 text-center text-brand-paper/50 border-l border-brand-paper/10 flex items-center justify-center gap-1.5">
                <X className="w-4 h-4" /> {fakeLabel}
              </div>
            </div>
            {compare.map((row, i) => {
              const labelText = getLocalizedText(row.label, lang, legacyCompare?.[i]?.label);
              const isHeating = labelText.includes('加熱') || labelText.toLowerCase().includes('heating');
              return (
                <div
                  key={i}
                  className={`grid grid-cols-3 text-xs sm:text-sm border-t ${
                    isHeating
                      ? 'border-brand-cinnabar/40 bg-brand-cinnabar/[0.08] shadow-[inset_0_0_20px_rgba(184,59,38,0.1)]'
                      : 'border-brand-paper/10'
                  }`}
                >
                  <div className={`p-4 font-serif text-brand-paper/80 flex items-center gap-2 ${isHeating ? 'font-bold' : ''}`}>
                    {isHeating && <Flame className="w-4 h-4 text-brand-cinnabar" />}
                    <span>{labelText}</span>
                    {isHeating && (
                      <span className="ml-1.5 text-[10px] bg-brand-cinnabar text-brand-paper px-1.5 py-0.5 rounded-sm tracking-wider">
                        安全關鍵
                      </span>
                    )}
                  </div>
                  <div className={`p-4 text-brand-paper/70 font-sans border-l border-brand-paper/10 ${isHeating ? 'bg-brand-gold/10 font-medium' : 'bg-brand-gold/[0.04]'}`}>
                    {getLocalizedText(row.genuine, lang, legacyCompare?.[i]?.genuine)}
                  </div>
                  <div className={`p-4 font-sans border-l border-brand-paper/10 line-through decoration-brand-cinnabar/50 ${isHeating ? 'text-brand-cinnabar/80 bg-brand-cinnabar/5' : 'text-brand-paper/45'}`}>
                    {getLocalizedText(row.fake, lang, legacyCompare?.[i]?.fake)}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-6 text-center text-brand-paper/50 text-[13px] font-sans">{note}</p>
        </div>
      </div>
    </section>
  );
};

export default Authentic;
